import { useState, useCallback, useEffect } from 'react';
import { IconCheck } from '@tabler/icons-react';
import { Modal, Skeleton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useLogoutFunction } from '@propelauth/react';
import clsx from 'clsx';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { hasAccessGrantingStatus } from '~/common/stripe/plan-utils';
import { canStartPlanTrial } from '~/common/stripe/trial-eligibility';
import { CheckoutSuccessHandler } from './CheckoutSuccessHandler';
import { NoPaymentOrgEscapeHatch } from './NoPaymentOrgEscapeHatch';
import { SubscriptionSuspendedModal } from './SubscriptionSuspendedModal';

export function NoPaymentModal() {
  const signOut = useLogoutFunction();
  const projectId = useProjectId();

  // Build redirect paths based on current project context
  // Falls back to root if no project ID is available (user will be redirected to default project)
  const settingsPath = projectId ? `/project/${projectId}/settings` : '/';
  const successPath = projectId ? `/project/${projectId}/settings?checkout=success` : '/?checkout=success';
  const cancelPath = settingsPath;

  // Get all plan and subscription data from server via tRPC
  const { data: planData, isLoading: isLoadingPlans } = api.stripe.getPlansToDisplay.useQuery();
  const plans = planData?.plans ?? [];
  const isReturningUser = planData?.isReturningUser ?? false;
  const canTrial = planData?.canTrial ?? false;
  const hasTrialed = planData?.hasTrialed ?? false;
  const hasSubscribedBefore = planData?.hasSubscribedBefore ?? false;
  const currentPlanId = planData?.currentPlanId;

  const { activeOrg, orgHelper, orgMetadata } = useAuthData();
  const orgs = orgHelper?.getOrgs() ?? [];
  const hasMultipleOrgs = orgs.length > 1;
  const firstOrgId = orgs[0]?.orgId;

  // Check if user is blocked by a failed payment state and needs invoice recovery.
  const hasFailedSubscriptionNeedingRecovery =
    orgMetadata?.stripe?.subscriptions.some(
      (sub) => (sub.status === 'past_due' || sub.status === 'unpaid') && !hasAccessGrantingStatus(sub),
    ) ?? false;

  const [selectedPlanLookupKey, setSelectedPlanLookupKey] = useState<string | undefined>();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() => activeOrg?.orgId ?? firstOrgId ?? null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutPlanKey, setCheckoutPlanKey] = useState<string | undefined>();

  // Auto-select the middle plan when plans load
  useEffect(() => {
    const currentPlans = planData?.plans;
    if (currentPlans && currentPlans.length > 0 && selectedPlanLookupKey === undefined) {
      const middleIndex = Math.floor(currentPlans.length / 2);
      setSelectedPlanLookupKey(currentPlans[middleIndex]?.lookupKey);
    }
  }, [planData?.plans, selectedPlanLookupKey]);

  // Keep selectedOrgId in sync when auth data changes (e.g. after switching orgs in another tab)
  useEffect(() => {
    setSelectedOrgId(activeOrg?.orgId ?? firstOrgId ?? null);
  }, [activeOrg?.orgId, firstOrgId]);

  const selectedPlanTrialEligible =
    selectedPlanLookupKey ?
      canStartPlanTrial({
        canTrial,
        hasTrialed,
        hasSubscribedBefore,
        currentPlanId,
        targetPlanId: selectedPlanLookupKey,
      })
    : false;

  const anyTrialEligible = plans.some((plan) =>
    canStartPlanTrial({
      canTrial,
      hasTrialed,
      hasSubscribedBefore,
      currentPlanId,
      targetPlanId: plan.lookupKey,
    }),
  );

  // Track which plan's checkout is in progress to prevent multiple sessions and show correct loading state
  const updateActiveTeam = api.team.updateActiveTeam.useMutation({
    onSuccess: () => {
      window.location.href = window.location.origin;
    },
    onError: (error) => {
      notifications.show({
        title: 'Unable to switch organization',
        message: error.message,
        color: 'red',
      });
    },
  });

  // Create checkout session mutation (V1 standard Stripe Checkout API)
  const createCheckoutSession = api.stripe.createCheckoutSession.useMutation({
    onMutate: (variables) => {
      // Latch immediately to prevent double-clicks, tracking which plan
      setCheckoutPlanKey(variables.planLookupKey);
    },
    onSuccess: (data) => {
      // Redirect in same tab to prevent multiple checkout sessions from repeated clicks
      window.location.href = data.url;
    },
    onError: () => {
      // Reset latch on error so user can retry
      setCheckoutPlanKey(undefined);
      notifications.show({
        title: 'Error',
        message: 'Failed to start checkout. Please try again.',
        color: 'red',
      });
    },
  });

  // Callback for CheckoutSuccessHandler to notify processing state changes
  const handleProcessingChange = useCallback((processing: boolean) => {
    setIsProcessing(processing);
  }, []);

  const handleSwitchOrg = useCallback(() => {
    if (!selectedOrgId) {
      return;
    }
    updateActiveTeam.mutate({ teamId: selectedOrgId });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateActiveTeam is a new object every render
  }, [selectedOrgId]);

  // Show invoice recovery variant for failed-payment subscriptions.
  if (hasFailedSubscriptionNeedingRecovery) {
    return <SubscriptionSuspendedModal />;
  }

  // Show plan selection flow for all users (new and returning)
  return (
    <Modal
      opened={true}
      onClose={() => {}}
      withCloseButton={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      size={1000}
      centered
      padding={0}
      radius="lg"
      styles={{
        content: {
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 40px)',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'transparent',
        },
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        },
      }}
      className="[&_.mantine-Modal-content]:bg-white dark:[&_.mantine-Modal-content]:bg-zinc-950"
    >
      <CheckoutSuccessHandler onProcessingChange={handleProcessingChange} />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white dark:bg-zinc-950">
        {/* Header */}
        <div className="shrink-0 border-b border-zinc-200 px-10 py-8 text-center dark:border-zinc-800">
          <h2 className="mb-2 text-2xl font-bold tracking-tight text-zinc-950 dark:text-zinc-50">
            {isReturningUser ? 'Reactivate Your Subscription' : 'Start using Stably'}
          </h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            {isReturningUser ?
              'Continue using Stably with full access to all features.'
            : 'Ship reliable software faster with AI-powered testing. Cancel anytime.'}
          </p>
          {anyTrialEligible && (
            <div className="mt-3 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              30-day free trial available — no payment method required
            </div>
          )}
        </div>

        {/* Plan Selection */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-zinc-50/50 px-10 py-8 dark:bg-zinc-900/20">
          <div className="grid grid-cols-3 gap-6">
            {isLoadingPlans ?
              // Loading skeleton - 3 plan card placeholders
              Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="flex min-h-[320px] flex-col rounded-xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950"
                >
                  <div className="mb-4 min-h-[120px]">
                    <div className="mb-3 flex items-center gap-2">
                      <Skeleton
                        height={24}
                        width={80}
                        radius="sm"
                      />
                    </div>
                    <Skeleton
                      height={40}
                      width={120}
                      radius="sm"
                      className="mb-2"
                    />
                    <Skeleton
                      height={16}
                      width="90%"
                      radius="sm"
                    />
                  </div>
                  <hr className="my-4 border-zinc-100 dark:border-zinc-800" />
                  <div className="flex flex-1 flex-col gap-3">
                    {Array.from({ length: 4 }).map((__, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-center gap-3"
                      >
                        <Skeleton
                          height={16}
                          width={16}
                          radius="sm"
                          className="shrink-0"
                        />
                        <Skeleton
                          height={16}
                          width={`${70 + (featureIndex % 3) * 10}%`}
                          radius="sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            : plans.map((plan) => {
                const isSelected = selectedPlanLookupKey === plan.lookupKey;
                const isRecommended = plan.recommended === true;
                const isPopular = plan.popular === true;

                return (
                  <div
                    key={plan.lookupKey}
                    onClick={() => setSelectedPlanLookupKey(plan.lookupKey)}
                    className={clsx(
                      'relative flex min-h-[320px] cursor-pointer flex-col rounded-xl border bg-white p-6 transition-all duration-200 dark:bg-zinc-950',
                      isSelected ?
                        'border-2 border-zinc-900 shadow-md dark:border-zinc-300'
                      : 'border border-zinc-200 shadow-sm hover:border-zinc-300 hover:shadow-md dark:border-zinc-800 dark:hover:border-zinc-700',
                    )}
                  >
                    {/* Active Plan Indicator Badge */}
                    {(isPopular || isRecommended) && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border border-zinc-200 bg-white px-3 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-900 shadow-sm dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100">
                        {isPopular ? 'Popular' : 'Recommended'}
                      </div>
                    )}

                    {/* Plan Header */}
                    <div className="mb-4 min-h-[120px]">
                      <h3 className="mb-2 text-lg font-bold text-zinc-950 dark:text-zinc-50">{plan.name}</h3>
                      <div className="mb-2 flex items-baseline gap-1">
                        <span className="text-4xl font-bold tracking-tight text-zinc-950 dark:text-zinc-50">
                          {plan.price}
                        </span>
                        <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{plan.period}</span>
                      </div>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400">{plan.description}</p>
                    </div>

                    <hr className="my-4 border-zinc-200 dark:border-zinc-800" />

                    {/* Features List */}
                    <div className="flex flex-1 flex-col gap-3">
                      {plan.features.map((feature) => (
                        <div
                          key={feature}
                          className="flex items-start gap-3"
                        >
                          <IconCheck
                            className="mt-0.5 h-4 w-4 shrink-0 text-zinc-900 dark:text-zinc-100"
                            stroke={2.5}
                          />
                          <span className="text-sm text-zinc-700 dark:text-zinc-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            }
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-col items-center border-t border-zinc-200 bg-white px-10 py-6 dark:border-zinc-800 dark:bg-zinc-950">
          <div className="flex w-full max-w-xl flex-col items-center gap-4">
            {selectedPlanLookupKey != null && (
              <button
                data-autofocus
                disabled={isProcessing || checkoutPlanKey != null || updateActiveTeam.isPending}
                onClick={() => {
                  if (checkoutPlanKey != null) {
                    return;
                  }
                  createCheckoutSession.mutate({
                    planLookupKey: selectedPlanLookupKey,
                    successPath,
                    cancelPath,
                  });
                }}
                className="flex h-11 w-full cursor-pointer items-center justify-center rounded-md bg-zinc-900 px-8 text-sm font-medium text-zinc-50 shadow transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-950 disabled:pointer-events-none disabled:opacity-50 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-zinc-200 dark:focus-visible:ring-zinc-300"
              >
                {checkoutPlanKey != null && createCheckoutSession.isPending ?
                  <div className="flex items-center gap-2">
                    <svg
                      className="-ml-1 mr-3 h-4 w-4 animate-spin text-white dark:text-zinc-900"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processing...
                  </div>
                : selectedPlanTrialEligible ?
                  'Start 30-day free trial'
                : 'Get Started'}
              </button>
            )}

            <p className="text-xs text-zinc-500 dark:text-zinc-400">Extra on-demand usage is available for each plan</p>

            <NoPaymentOrgEscapeHatch
              activeOrgId={activeOrg?.orgId}
              isSwitching={updateActiveTeam.isPending}
              orgs={hasMultipleOrgs ? orgs : undefined}
              selectedOrgId={selectedOrgId}
              onSelectedOrgChange={setSelectedOrgId}
              onSwitchOrg={handleSwitchOrg}
              onLogout={() => {
                void signOut(true);
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
